import { useCallback, useState, useEffect, useRef } from 'react';

import { Show } from '@rails/upload';
import { useFind, useResult } from '@rails/document/hooks';
import { useIsFullscreen } from 'fullscreen-slice/selectors';

import createT from 'T';
const T = createT();
T({});
import { useSession } from '~Session';
import { useFullscreenApi } from 'fullscreen-slice/actions';
import { FullscreenIcon } from '@/icons';

/* rotationList = rotate-0 rotate-90 rotate-180 rotate-270 border self-start */

const Slider = ({ t1 = 20000, t2 = 4000 }) => {
  const [imageId, setImageId] = useState(null);

  const params = { t1, t2, setImageId, imageId };
  useUpdateImage(params);
  return imageId ? (
    <RealSlider {...{ imageId, setImageId }} />
  ) : (
    <div>{"Pas d'image"}</div>
  );
};

const RealSlider = ({ imageId, setImageId }) => {
  const fetcher = useFind(imageId, 'shares', { includes: { upload: 'user' } });
  const share = useResult(fetcher);
  const upload = share?.upload;
  const container = useRef();
  // lors du retour du fullscreen, container ne changeait pas de taille => min-h-0
  return (
    <div
      id='slider'
      className={`bg-black text-white min-h-0 flex flex-grow flex-col relative justify-center items-center`}
      ref={container}
    >
      <Fullscreen />
      <Infos {...{ fetcher }} />
      <Show
        upload={upload}
        container={container}
      />
    </div>
  );
};

const Fullscreen = () => {
  const isFullscreen = useIsFullscreen();
  const { toggle } = useFullscreenApi();
  return (
    !isFullscreen /* daisyUi bug, je ne peux appliquer directement le style au bouton, sans un glitch à cause des translates */ && (
      <div className='absolute z-20 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
        <button
          className='btn'
          onClick={() => toggle('slider')}
        >
          <FullscreenIcon />
        </button>
      </div>
    )
  );
};

const Infos = ({ fetcher }) => {
  const share = useResult(fetcher);
  const userName = share?.upload?.user.name;
  return (
    <>
      <div className='absolute top-0 left-0 text-6xl z-10 bg-transparent flex flex-col items-center'>
        <DateTime />
        <div className='h-5' />
      </div>
      <div className='absolute top-0 right-0 z-10 bg-transparent flex flex-row items-center'>
        <DayOrNight />
        <Panasonic />
      </div>
      {userName && (
        <div className='absolute bottom-0 right-0 text-6xl z-10 bg-transparent text-shadow-lg text-shadow-black text-white'>
          <Sender {...{ userName }} />
        </div>
      )}
    </>
  );
};

const DateTime = () => {
  const [date, setDate] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setDate(new Date()), 10000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className='text-shadow-lg text-shadow-black text-white'>
      <div>
        {date.toLocaleDateString('fr', {
          weekday: 'long',
        })}
      </div>
      <div>
        {date.toLocaleDateString('fr', {
          day: 'numeric',
        })}
      </div>
      <div>
        {date.toLocaleDateString('fr', {
          month: 'long',
        })}
      </div>
      <div>
        {date.toLocaleDateString('fr', {
          year: 'numeric',
        })}
      </div>
      <div className='text-4xl bg-transparent text-shadow-lg text-shadow-white text-black'>
        {date.toLocaleTimeString('fr', {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>
    </div>
  );
};

import Thermometer from '@/components/Thermometer';

const Panasonic = () => {
  const [enabled, setEnabled] = useState(false);
  const [value, setValue] = useState(0);
  useEffect(() => {
    const updateTemperature = () => {
      fetch('panasonic-state')
        .then((r) => r.json())
        .then((temperatures) => {
          setValue(temperatures.inside);
          setEnabled(Boolean(temperatures.inside));
        })
        .catch(() => {
          setEnabled(false);
        });
    };
    updateTemperature();
    const timer = setInterval(updateTemperature, 30 * 1000);
    return () => clearInterval(timer);
  }, []);
  if (!enabled) return null;
  return (
    <Thermometer
      min={20}
      max={35}
      value={value}
    />
  );
};

function isDayOrNight(lat, lon) {
  return fetch(
    `https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lon}&formatted=0`,
  )
    .then((r) => r.json())
    .then(({ results }) => {
      const now = new Date();
      const sunrise = new Date(results.sunrise);
      const sunset = new Date(results.sunset);

      return now >= sunrise && now < sunset ? 'day' : 'night';
    });
}

import moon from '@/../img/moon.png';
import sun from '@/../img/sun.png';
const DayOrNight = () => {
  const [isDay, setIsDay] = useState(false);
  useEffect(() => {
    const update = async () => {
      const response = await isDayOrNight(
        45.166299929473126,
        5.764795645121559,
      );
      setIsDay(response === 'day');
    };
    const timer = setInterval(update, 60000);
    update();
    return () => clearInterval(timer);
  }, []);
  return (
    <img
      className='md:size-75 size-25'
      src={isDay ? sun : moon}
    />
  );
};

const Sender = ({ userName }) => (
  <>
    <div>de</div>
    <div>{userName}</div>
  </>
);

const useUpdateImage = (params) => {
  const { setImageId, t1, imageId } = params;
  const {
    cable: { say, useListener },
  } = useSession();
  const forced = useForcedImage(params);

  useEffect(() => {
    !imageId && say('updateImage');
  }, [say, imageId]);
  useEffect(() => {
    if (forced) return;
    const timer = setTimeout(() => say('updateImage', { imageId }), t1);
    return () => clearTimeout(timer);
  }, [forced, say, t1, imageId]);

  const listener = useCallback(
    ({ update_image }) => {
      if (update_image) {
        setImageId(update_image);
      }
    },
    [setImageId],
  );

  useListener(listener);
};

const useForcedImage = ({ setImageId, t2 }) => {
  const {
    cable: { useListener },
  } = useSession();
  const [forced, setForced] = useState(false);

  useEffect(() => {
    if (forced) {
      const timer = setTimeout(() => setForced(false), t2);
      return () => {
        setForced(false);
        clearTimeout(timer);
      };
    }
  }, [forced, t2]);

  const listener = useCallback(
    ({ forced_image }) => {
      if (forced_image) {
        setForced(true);
        setImageId(forced_image);
      } else if (forced_image === null) {
        setForced(false);
      }
    },
    [setImageId],
  );
  useListener(listener);
  return forced;
};
export default Slider;
